import {
  PERMISSION_SEGMENT_COUNT,
  SEGMENT_WILDCARD,
  TAIL_WILDCARD,
  type ParsedPermission,
  type PermissionExpression,
} from "./contracts";

function segments(expression: PermissionExpression): string[] {
  return expression.split(".");
}

/** True when the expression is non-empty and has no blank segments. */
export function isValidPermissionExpression(
  expression: string,
): expression is PermissionExpression {
  if (expression.length === 0) {
    return false;
  }

  const parts = segments(expression);

  if (parts.some((part) => part.length === 0)) {
    return false;
  }

  // `**` is only meaningful as the final segment.
  return parts.every(
    (part, index) => part !== TAIL_WILDCARD || index === parts.length - 1,
  );
}

/**
 * Splits a canonical `<module>.<submodule>.<screen>.<action>` expression.
 * Returns `undefined` for wildcarded or non-canonical expressions — callers
 * that need structure should not be handed a pattern.
 */
export function parsePermission(
  expression: PermissionExpression,
): ParsedPermission | undefined {
  const parts = segments(expression);

  if (parts.length !== PERMISSION_SEGMENT_COUNT) {
    return undefined;
  }

  if (
    parts.some((part) => part === SEGMENT_WILDCARD || part === TAIL_WILDCARD)
  ) {
    return undefined;
  }

  const [module, submodule, screen, action] = parts as [
    string,
    string,
    string,
    string,
  ];

  return { module, submodule, screen, action };
}

/**
 * Wildcard-aware comparison. Symmetric: a wildcard on either side matches a
 * concrete segment on the other, so a `fleet.**` grant satisfies a
 * `fleet.vehicles.list.view` requirement and a `fleet.vehicles.*.view`
 * requirement is satisfied by a concrete `fleet.vehicles.list.view` grant.
 *
 * `**` matches one or more segments, never zero.
 */
export function matchesPermission(
  granted: PermissionExpression,
  requested: PermissionExpression,
): boolean {
  if (granted === requested) {
    return true;
  }

  const left = segments(granted);
  const right = segments(requested);

  const shortest = Math.min(left.length, right.length);

  for (let index = 0; index < shortest; index += 1) {
    const a = left[index] as string;
    const b = right[index] as string;

    // A trailing `**` swallows whatever remains on the other side, provided
    // there is at least one segment left to swallow.
    if (a === TAIL_WILDCARD) {
      return index === left.length - 1 && index < right.length;
    }

    if (b === TAIL_WILDCARD) {
      return index === right.length - 1 && index < left.length;
    }

    if (a === b || a === SEGMENT_WILDCARD || b === SEGMENT_WILDCARD) {
      continue;
    }

    return false;
  }

  // Every compared segment matched; only equal-length expressions are a hit.
  return left.length === right.length;
}
